import * as React from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames'

import Moment from 'react-moment'
import { Table } from 'reactstrap'
import { Tooltip } from 'react-tippy'

import { RootState } from '../../../store'
import { Accident } from 'src/lib/interfaces/Accident.interface'
import { Claim } from 'src/lib/interfaces/Claim.interface'
import { Location } from 'src/lib/interfaces/Location.interface'
import * as CurrencyFormat from 'react-currency-format'
import { AccidentFilterParameters } from 'src/lib/interfaces/AccidentFilterParameters.interface'

import VIN from 'src/common/VIN.component'

import * as appSelectors from '../../../store/app/selectors'
import { updateAccidentFilterParamsDispatchCommand } from '../../../store/accidents/actions'

const get = require('lodash/get')
const map = require('lodash/map')
const find = require('lodash/find')
const first = require('lodash/first')
const filter = require('lodash/filter')
const compact = require('lodash/compact')
const forEach = require('lodash/forEach')

export interface AccidentTableProps {
  accidents: Accident[]
  onSelect: (accident: Accident) => void
  loadingFailed: boolean
  isLoading: boolean

  // container
  accidentsFilterParams: AccidentFilterParameters
  updateAccidentFilterParams: (filterParams: AccidentFilterParameters) => void
}

class AccidentTable extends React.PureComponent<AccidentTableProps> {
  currencyCell = (cost: number) => {
    return (
      <CurrencyFormat
        prefix="$"
        value={cost}
        decimalScale={2}
        displayType="text"
        thousandSeparator={true}
        fixedDecimalScale={true}
      />
    )
  }

  renderErac = (accident: Accident) => {
    if (!accident.claims) {
      return 'NO CLAIMS'
    }

    let erac
    const firstClaim = get(accident, 'claims[0]')
    if (firstClaim) {
      erac = firstClaim.enterpriseId || 'N/A'
    }

    if (accident.claims.length > 1) {
      const allEracs = map(accident.claims, claim => claim.enterpriseId || 'N/A')
      return (
        <Tooltip followCursor className="d-flex" trigger="mouseenter" html={
          <div className="ggmouseover d-flex flex-column p-3">
            <span>{allEracs.join(', ')}</span>
          </div>
        }>
          <span className="d-flex align-items-center">
            {erac}
            {get(accident, 'claims.length', 0) > 1 &&
              <div className="badge badge-secondary ml-2">{`+${accident.claims.length - 1}`}</div>
            }
          </span>
        </Tooltip>
      )
    }

    return erac
  }

  renderVin = (accident: Accident) => {
    // Get the vin of the first accident, and handles only the last 6 digits
    const getVin = (claim: Claim) => {
      let vin
      if (claim.vehicleVin) {
        const rawVin = claim.vehicleVin
        if (rawVin.length < 6) {
          vin = rawVin
        }
        else {
          vin = rawVin.substr(rawVin.length - 6, rawVin.length - 1)
        }
      }

      return vin
    }

    if (accident.claims && accident.claims.length) {
      if (accident.claims.length > 1) {
        const allVins = compact(map(accident.claims, claim => getVin(claim)))
        return (
          <Tooltip followCursor className="d-flex" trigger="mouseenter" html={
            <div className="ggmouseover d-flex flex-column p-3">
              <span>{allVins.join(', ')}</span>
            </div>
          }>
            <span>Multiple vin</span>
          </Tooltip>
        )
      }
      else if (accident.claims[0].vehicleVin) {
        const rawVin = accident.claims[0].vehicleVin!

        if (rawVin.length < 6) {
          return <VIN vin={rawVin} />
        }
        else {
          const vin = rawVin.substr(rawVin.length - 6, rawVin.length - 1)
          return <VIN vin={vin} />
        }
      }
    }

    return 'NO VIN'
  }

  getStatus = (accident: Accident) => {
    const firstClaimStatus = get(accident, 'claims[0].claimStatus', {})
    const matchingStatusClaims = filter(accident.claims, { claimStatus: { id: firstClaimStatus.id }})

    if (matchingStatusClaims.length === get(accident, 'claims.length', 0)) {
      return firstClaimStatus.name
    }

    return 'Multiple status'
  }

  getAccidentCost = (accident: Accident) => {
    let cost = 0
    forEach(accident.claims, claim => {
      cost += claim.damage
    })

    return cost
  }

  renderRow = (accident: Accident, onClick: (accident: Accident) => void) => {
    return (
      <tr key={accident.id} onClick={() => onClick(accident)} style={{ cursor: 'pointer' }}>
        <td>{this.renderErac(accident)}</td>
        <td><Moment date={get(accident, 'lossDate')} format="MM.DD.YYYY"/></td>
        <td>{get(accident, 'claims[0].claimType.name')}</td>
        <td>{this.renderVin(accident)}</td>
        <td>{get(accident, 'locationPosition.location.name', get(accident, 'location.name'))}</td>
        <td>{this.getStatus(accident)}</td>
        <td className="float-right">
          <CurrencyFormat
            prefix={'$'}
            decimalScale={2}
            displayType="text"
            thousandSeparator={true}
            fixedDecimalScale={true}
            value={this.getAccidentCost(accident)}
          />
        </td>
        {/* <td>{accident}</td>*/}
        {/* <td>Awaiting Report</td>*/}
        {/* <td>{accident.claim}</td>*/}
        {/*<td>APPROVED</td>*/}
        {/*<td className={'text-right'}>{this.currencyCell(accident.totalCost)}</td>*/}
      </tr>
    )
  }

  handleSortFilterChange = (sortBy: string): void => {
    const { accidentsFilterParams, updateAccidentFilterParams } = this.props

    let sortOrder = accidentsFilterParams.sortOrder
    if (!sortOrder || sortOrder === 'DESC') { sortOrder = 'ASC' }
    else if (sortOrder === 'ASC') { sortOrder = 'DESC' }

    updateAccidentFilterParams({
      ...accidentsFilterParams,
      sortBy,
      sortOrder
    })
  }

  render() {
    const { accidentsFilterParams } = this.props

    const getClassNameForKey = (key: string): string | undefined => {
      if (accidentsFilterParams.sortBy !== key) { return }

      return `-sorted -${(accidentsFilterParams.sortOrder || '').toLowerCase()}`
    }

    return (
      <div>
        <Table hover className="table table-striped">
          <thead>
            <tr>
              <th style={{width: '30px'}}>ERAC</th>
              <th
                style={{ width: '50px' }}
                onClick={() => this.handleSortFilterChange('lossDate')}
                className={classnames('-sortable', getClassNameForKey('lossDate'))}
              >
                Loss Date
              </th>
              <th style={{ width: '30px' }}>Type</th>
              <th style={{ width: '30px' }}>VIN</th>
              <th
                style={{ width: '30px' }}
                onClick={() => this.handleSortFilterChange('location')}
                className={classnames('-sortable', getClassNameForKey('location'))}
              >
                Location
              </th>
              {/* <th className="w-auto">Cost</th> */}
              {/* <th className="w-auto">Status</th> */}
              {/* <th className="w-auto">.</th> */}
              <th style={{width: '30px'}}>Status</th>
              {/* <th className={'float-right'} style={{width: '30px'}}>Cost</th> */}
              <th
                style={{width: '30px'}}
                onClick={() => this.handleSortFilterChange('damage')}
                className={classnames('-sortable', getClassNameForKey('damage'))}
              >
                Cost
              </th>
            </tr>
          </thead>
          <tbody>
            {this.props.isLoading && renderLoadingCell()}
            {this.props.loadingFailed && renderErrorCell()}
            {!this.props.loadingFailed && this.props.accidents.map(accident => this.renderRow(accident, this.props.onSelect))}
          </tbody>
        </Table>
      </div>
    )
  }
}
const renderLoadingCell = () => {
  return (
    <tr>
      <td colSpan={8} className={'text-center'}>
        Loading...
      </td>
    </tr>
  )
}

const renderErrorCell = () => {
  return (
    <tr>
      <td colSpan={8} className='text-center text-danger'>
        <h5>failed to load</h5>
      </td>
    </tr>
  )
}

const mapStateToProps = (state: RootState)  => ({
  accidentsFilterParams: state.accidents.filterParams
})

const mapDispatchToProps = (dispatch) => ({
  updateAccidentFilterParams: filterParams => dispatch(updateAccidentFilterParamsDispatchCommand(filterParams))
})

export default connect(mapStateToProps, mapDispatchToProps)(AccidentTable)



// WEBPACK FOOTER //
// ./src/routes/accidents/components/AccidentTable.components.tsx